import { z } from 'zod';
export const contactKinds = ['person', 'organization'];
export const matterStatuses = ['open', 'pending', 'closed'];
export const activeTaskStatuses = ['todo', 'in_progress', 'completed'];
export const taskStatuses = [...activeTaskStatuses, 'cancelled'];
export const priorities = ['low', 'normal', 'high', 'urgent'];
export const auditEntityTypes = [
    'matter',
    'contact',
    'task',
    'note',
    'matter_party',
    'communication',
    'time_entry',
    'intake',
    'document',
    'calendar_event',
    'conflict_check',
    'time_tracker',
];
export const activeIntakeStatuses = ['new', 'contacted', 'consultation'];
export const intakeStatuses = [...activeIntakeStatuses, 'retained', 'declined'];
export const intakeDispositionStatuses = [...activeIntakeStatuses, 'declined'];
export const calendarEventTypes = ['deadline', 'hearing', 'meeting', 'internal'];
export const matterPartyRoles = [
    'client',
    'co_client',
    'opposing_party',
    'opposing_counsel',
    'witness',
    'expert',
    'court',
    'agency',
    'other',
];
export const communicationChannels = [
    'email',
    'phone',
    'video',
    'in_person',
    'letter',
    'portal',
    'other',
];
export const communicationDirections = ['inbound', 'outbound', 'internal'];
const optionalText = z.string().trim().max(500).optional().or(z.literal(''));
const optionalDateTime = z.iso.datetime().optional().or(z.literal(''));
export const loginSchema = z.object({
    email: z.email().max(254),
    password: z.string().min(8).max(256),
});
export const signupPasswordPolicy = {
    minLength: 12,
    maxLength: 256,
    requirements: 'Use 12–256 characters with uppercase and lowercase letters, a number, and a symbol.',
};
export const signupPasswordSchema = z
    .string()
    .refine((password) => password.length >= signupPasswordPolicy.minLength &&
    password.length <= signupPasswordPolicy.maxLength &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[\x21-\x2f\x3a-\x40\x5b-\x60\x7b-\x7e]/.test(password), { message: signupPasswordPolicy.requirements });
const webAuthnCredentialSchema = z
    .object({
    id: z.string().min(1).max(4096),
    rawId: z.string().min(1).max(4096),
    type: z.literal('public-key'),
    response: z.record(z.string(), z.unknown()),
    clientExtensionResults: z.record(z.string(), z.unknown()).optional(),
    authenticatorAttachment: z.enum(['platform', 'cross-platform']).nullable().optional(),
})
    .passthrough();
export const passkeyLoginStartSchema = z.object({
    email: z.email().max(254),
});
export const passkeyLoginCompleteSchema = z.object({
    email: z.email().max(254),
    credential: webAuthnCredentialSchema,
});
export const passkeyRegistrationCompleteSchema = z.object({
    credential: webAuthnCredentialSchema,
});
export const signupSchema = z.object({
    displayName: z.string().trim().min(2).max(150),
    organizationName: z.string().trim().min(2).max(150),
    edition: z.enum(['firm', 'mission']),
    email: z.email().max(254),
    password: signupPasswordSchema,
});
export const signupConfirmationSchema = z.object({
    registrationId: z.uuid(),
    confirmationCode: z.string().trim().min(1).max(2048),
    password: signupPasswordSchema,
});
export const switchTenantSchema = z.object({
    tenantId: z.uuid(),
});
export const accountProfileUpdateSchema = z
    .object({
    displayName: z.string().trim().min(2).max(150),
})
    .strict();
export const organizationMembershipRoles = [
    'owner',
    'attorney',
    'staff',
    'advocate',
    'viewer',
];
export const organizationAssignableRoles = organizationMembershipRoles;
export const organizationUserRoleUpdateSchema = z
    .object({
    role: z.enum(organizationMembershipRoles),
})
    .strict();
export const organizationSettingsUpdateSchema = z
    .object({
    name: z.string().trim().min(2).max(200),
})
    .strict();
export const organizationFeatureKeys = ['time_tracking'];
export const organizationFeatureSettingsUpdateSchema = z
    .object({
    enabled: z.boolean(),
    expectedSettingsVersion: z.number().int().positive(),
})
    .strict();
export const organizationInvitationInputSchema = z
    .object({
    email: z
        .email()
        .max(254)
        .transform((email) => email.toLowerCase()),
    role: z.enum(organizationAssignableRoles),
})
    .strict();
export const emailDeliveryCommandSchema = z
    .object({
    schemaVersion: z.literal(1),
    deliveryId: z.uuid(),
    message: z
        .object({
        to: z.email().max(254),
        subject: z.string().min(1).max(998),
        text: z.string().min(1).max(100_000),
        html: z.string().min(1).max(100_000),
    })
        .strict(),
})
    .strict();
export const organizationInvitationTokenSchema = z
    .string()
    .min(40)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/);
export const organizationInvitationDetailsInputSchema = z
    .object({ token: organizationInvitationTokenSchema })
    .strict();
export const organizationInvitationAcceptanceSchema = z
    .object({
    displayName: z.string().trim().min(2).max(150),
    password: z.string().min(8).max(256),
})
    .strict();
export const organizationInvitationConfirmationSchema = z
    .object({
    displayName: z.string().trim().min(2).max(150),
    confirmationCode: z.string().trim().min(1).max(2048),
    password: z.string().min(8).max(256),
})
    .strict();
export const organizationInvitationAcceptanceRequestSchema = organizationInvitationAcceptanceSchema
    .extend({ token: organizationInvitationTokenSchema })
    .strict();
export const organizationInvitationConfirmationRequestSchema = organizationInvitationConfirmationSchema
    .extend({ token: organizationInvitationTokenSchema })
    .strict();
export const systemTenantDirectoryQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterCreatedAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Tenant-directory cursors require both afterCreatedAt and afterId.',
        });
    }
});
export const systemTenantParamSchema = z.object({ tenantId: z.uuid() }).strict();
export const planApiErrorCodes = {
    limitExceeded: 'PLAN_API_LIMIT_EXCEEDED',
    notIncluded: 'PLAN_API_NOT_INCLUDED',
};
const planTierKeySchema = z
    .string()
    .trim()
    .min(2)
    .max(50)
    .regex(/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/, 'Use lowercase letters, numbers, dots, hyphens, or underscores.');
export const planTierApiSelectionSchema = z
    .object({
    apiId: z.string().min(1).max(300),
    monthlyLimit: z.number().int().positive().max(1_000_000_000).nullable(),
})
    .strict();
const planTierFieldsSchema = z
    .object({
    name: z.string().trim().min(2).max(100),
    description: z.string().trim().max(500),
    available: z.boolean(),
    signupDefault: z.boolean(),
    apis: z.array(planTierApiSelectionSchema),
})
    .strict()
    .superRefine((value, context) => {
    const seen = new Set();
    value.apis.forEach((api, index) => {
        if (seen.has(api.apiId)) {
            context.addIssue({
                code: 'custom',
                path: ['apis', index, 'apiId'],
                message: 'Each API can be selected only once.',
            });
        }
        seen.add(api.apiId);
    });
    if (value.signupDefault && !value.available) {
        context.addIssue({
            code: 'custom',
            path: ['available'],
            message: 'The signup default tier must be available.',
        });
    }
});
export const planTierCreateSchema = planTierFieldsSchema.safeExtend({
    key: planTierKeySchema,
});
export const planTierUpdateSchema = planTierFieldsSchema.safeExtend({
    expectedVersion: z.number().int().positive(),
});
export const planTierKeyParamSchema = z.object({ key: planTierKeySchema }).strict();
export const contactInputSchema = z
    .object({
    kind: z.enum(contactKinds).default('person'),
    firstName: optionalText,
    lastName: optionalText,
    organizationName: optionalText,
    email: z.email().max(254).optional().or(z.literal('')),
    phone: optionalText,
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
})
    .superRefine((value, context) => {
    if (value.kind === 'person' && !value.firstName && !value.lastName) {
        context.addIssue({ code: 'custom', message: 'A person needs a first or last name.' });
    }
    if (value.kind === 'organization' && !value.organizationName) {
        context.addIssue({ code: 'custom', message: 'An organization name is required.' });
    }
});
export const contactUpdateInputSchema = contactInputSchema.safeExtend({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const contactDirectoryQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    kind: z.enum(['all', 'person', 'organization']).default('all'),
    tag: z.string().trim().max(40).default(''),
    afterName: z.string().max(240).optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.afterName === undefined) !== (value.afterId === undefined)) {
        context.addIssue({
            code: 'custom',
            message: 'Contact directory cursors require both afterName and afterId.',
        });
    }
});
export const contactOptionQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
})
    .strict();
export const contactHistoryQuerySchema = z
    .object({
    afterChangedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterChangedAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Contact-history cursors require both afterChangedAt and afterId.',
        });
    }
});
export const teamOptionQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    view: z.enum(['assignable', 'all']).default('assignable'),
})
    .strict();
export const matterInputSchema = z.object({
    clientContactId: z.uuid(),
    name: z.string().trim().min(2).max(200),
    description: z.string().trim().max(5000).default(''),
    practiceArea: z.string().trim().min(2).max(100),
    status: z.enum(matterStatuses).default('open'),
    stage: z.string().trim().min(2).max(100).default('Active'),
    responsibleMembershipId: z.uuid().optional().or(z.literal('')),
    openedAt: z.iso.date(),
    closeDate: z.iso.date().optional().or(z.literal('')),
    rateCents: z.number().int().min(0).max(1_000_000).default(0),
});
export const matterUpdateInputSchema = matterInputSchema.extend({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const matterTransitionInputSchema = z.object({
    status: z.enum(matterStatuses),
    stage: z.string().trim().min(2).max(100),
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const matterCustomFieldTypes = ['text', 'number', 'date', 'boolean', 'select'];
export const matterCustomFieldDefinitionInputSchema = z
    .object({
    name: z.string().trim().min(2).max(100),
    description: z.string().trim().max(500).default(''),
    fieldType: z.enum(matterCustomFieldTypes),
    options: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
})
    .superRefine((value, context) => {
    const distinctOptions = new Set(value.options.map((option) => option.toLocaleLowerCase()));
    if (distinctOptions.size !== value.options.length) {
        context.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'Select options must be unique.',
        });
    }
    if (value.fieldType === 'select' && value.options.length === 0) {
        context.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'Select fields require at least one option.',
        });
    }
    if (value.fieldType !== 'select' && value.options.length > 0) {
        context.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'Only select fields accept options.',
        });
    }
});
const matterCustomFieldValueSchema = z.union([
    z.string().max(5000),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
export const matterCustomFieldChangesInputSchema = z.object({
    changes: z
        .array(z.object({
        definitionId: z.uuid(),
        value: matterCustomFieldValueSchema,
    }))
        .min(1)
        .max(50)
        .superRefine((changes, context) => {
        const ids = new Set(changes.map((change) => change.definitionId));
        if (ids.size !== changes.length) {
            context.addIssue({ code: 'custom', message: 'Each field may be changed only once.' });
        }
    }),
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const matterOptionQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    view: z.enum(['active', 'all']).default('active'),
})
    .strict();
export const matterDirectoryQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    scope: z.enum(['all', 'mine', 'unassigned']).default('all'),
    view: z.enum(['all', 'active', 'open', 'pending', 'closed']).default('active'),
    afterStatusRank: z.enum(['0', '1', '2']).optional(),
    afterUpdatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterStatusRank, value.afterUpdatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Matter directory cursors require afterStatusRank, afterUpdatedAt, and afterId.',
        });
    }
});
export const contactMatterDirectoryQuerySchema = z
    .object({
    view: z.enum(['all', 'active', 'open', 'pending', 'closed']).default('active'),
    afterStatusRank: z.enum(['0', '1', '2']).optional(),
    afterUpdatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterStatusRank, value.afterUpdatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Contact matter cursors require afterStatusRank, afterUpdatedAt, and afterId.',
        });
    }
});
export const matterCommunicationDirectoryQuerySchema = z
    .object({
    afterOccurredAt: z.iso.datetime().optional(),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterOccurredAt, value.afterCreatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Matter communication cursors require afterOccurredAt, afterCreatedAt, and afterId.',
        });
    }
});
export const contactCommunicationDirectoryQuerySchema = z
    .object({
    afterOccurredAt: z.iso.datetime().optional(),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterOccurredAt, value.afterCreatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Contact communication cursors require afterOccurredAt, afterCreatedAt, and afterId.',
        });
    }
});
export const matterPartyDirectoryQuerySchema = z
    .object({
    afterRemoved: z.enum(['true', 'false']).optional(),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterRemoved, value.afterCreatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Matter party cursors require afterRemoved, afterCreatedAt, and afterId.',
        });
    }
});
export const matterHistoryDirectoryQuerySchema = z
    .object({
    afterChangedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterChangedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Matter history cursors require afterChangedAt and afterId.',
        });
    }
});
export const matterNoteDirectoryQuerySchema = z
    .object({
    afterPinned: z.enum(['true', 'false']).optional(),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterPinned, value.afterCreatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Matter note cursors require afterPinned, afterCreatedAt, and afterId.',
        });
    }
});
export const taskInputSchema = z.object({
    matterId: z.uuid().optional().or(z.literal('')),
    title: z.string().trim().min(2).max(240),
    description: z.string().trim().max(3000).default(''),
    dueAt: optionalDateTime,
    priority: z.enum(priorities).default('normal'),
    status: z.enum(activeTaskStatuses).default('todo'),
    assignedMembershipId: z.uuid().optional().or(z.literal('')),
});
export const taskTemplateInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(1000).default(''),
    items: z
        .array(z.object({
        title: z.string().trim().min(2).max(240),
        description: z.string().trim().max(3000).default(''),
        dueOffsetDays: z.number().int().min(0).max(3650),
        priority: z.enum(priorities).default('normal'),
    }))
        .min(1)
        .max(25),
});
export const taskTemplateApplicationInputSchema = z.object({
    templateId: z.uuid(),
    anchorAt: z.iso.datetime(),
    assignedMembershipId: z.uuid().optional().or(z.literal('')),
    idempotencyKey: z.uuid(),
});
export const taskListQuerySchema = z
    .object({
    scope: z.enum(['all', 'mine', 'unassigned']).default('all'),
    view: z.enum(['all', 'open', 'todo', 'in_progress', 'completed', 'cancelled']).default('all'),
    priority: z.enum(['all', ...priorities]).default('all'),
    timing: z.enum(['all', 'overdue', 'next_7_days', 'unscheduled']).default('all'),
    q: z.string().trim().max(200).default(''),
    afterTerminal: z.enum(['0', '1']).optional(),
    afterDueAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterTerminal, value.afterDueAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Task cursors require afterTerminal, afterDueAt, and afterId.',
        });
    }
});
export const taskHistoryQuerySchema = z
    .object({
    view: z.enum(['changes', 'status']).default('changes'),
    afterChangedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterChangedAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Task-history cursors require both afterChangedAt and afterId.',
        });
    }
});
export const taskDetailsInputSchema = taskInputSchema
    .pick({
    title: true,
    description: true,
    dueAt: true,
    priority: true,
    assignedMembershipId: true,
})
    .extend({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const noteInputSchema = z.object({
    matterId: z.uuid(),
    body: z.string().trim().min(1).max(20_000),
    isPinned: z.boolean().default(false),
});
export const noteAmendmentInputSchema = z.object({
    body: z.string().trim().min(1).max(20_000),
    reason: z.string().trim().min(2).max(1000),
});
export const timeEntryInputSchema = z.object({
    matterId: z.uuid(),
    workDate: z.iso.date(),
    durationMinutes: z.number().int().min(1).max(1440),
    description: z.string().trim().min(2).max(2000),
    billable: z.boolean().default(true),
});
export const timeTrackerStartInputSchema = z
    .object({
    matterId: z.uuid(),
    description: z.string().trim().min(2).max(1000),
})
    .strict();
export const timeTrackerStopInputSchema = z.object({ billable: z.boolean() }).strict();
export const timeEntryDirectoryQuerySchema = z
    .object({
    scope: z.enum(['all', 'mine']).default('all'),
    view: z.enum(['all', 'current', 'unbilled', 'nonbillable', 'void']).default('all'),
    q: z.string().trim().max(200).default(''),
    fromWorkDate: z.iso.date().optional(),
    throughWorkDate: z.iso.date().optional(),
    afterWorkDate: z.iso.date().optional(),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterWorkDate, value.afterCreatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Time-entry cursors require afterWorkDate, afterCreatedAt, and afterId.',
        });
    }
    if (value.fromWorkDate !== undefined &&
        value.throughWorkDate !== undefined &&
        value.fromWorkDate > value.throughWorkDate) {
        context.addIssue({
            code: 'custom',
            message: 'Time-entry work-date bounds must be chronological.',
        });
    }
});
export const intakeInputSchema = z.object({
    name: z.string().trim().min(2).max(200),
    email: z.email().max(254).optional().or(z.literal('')),
    phone: optionalText,
    source: z.string().trim().min(2).max(100),
    practiceArea: z.string().trim().min(2).max(100),
    status: z.enum(intakeStatuses).default('new'),
    nextFollowUpAt: optionalDateTime,
    summary: z.string().trim().max(5000).default(''),
});
export const intakeDirectoryQuerySchema = z
    .object({
    status: z.enum(intakeStatuses),
    timing: z.enum(['all', 'overdue', 'next_7_days', 'unscheduled']).default('all'),
    q: z.string().trim().max(200).default(''),
    afterFollowUpAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.afterFollowUpAt === undefined) !== (value.afterId === undefined)) {
        context.addIssue({
            code: 'custom',
            message: 'Intake directory cursors require both afterFollowUpAt and afterId.',
        });
    }
});
export const intakeHistoryQuerySchema = z
    .object({
    view: z.enum(['changes', 'dispositions']),
    afterChangedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterChangedAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Intake-history cursors require both afterChangedAt and afterId.',
        });
    }
});
export const intakeUpdateInputSchema = intakeInputSchema.extend({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const intakeDispositionInputSchema = z.object({
    status: z.enum(intakeDispositionStatuses),
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const intakeConversionInputSchema = z.object({
    expectedRecordVersion: z.number().int().min(1),
    contact: contactInputSchema,
    matter: z.object({
        name: z.string().trim().min(2).max(200),
        practiceArea: z.string().trim().min(2).max(100),
        stage: z.string().trim().min(2).max(100).default('Opening'),
        responsibleMembershipId: z.uuid().optional().or(z.literal('')),
        openedAt: z.iso.date(),
        rateCents: z.number().int().min(0).max(1_000_000).default(0),
    }),
    conflictReviewAcknowledged: z.literal(true),
});
export const documentInputSchema = z.object({
    matterId: z.uuid(),
    name: z.string().trim().min(1).max(255),
    category: z.string().trim().min(2).max(100),
    sizeBytes: z.number().int().min(0).max(5_000_000_000).default(0),
});
export const documentDirectoryQuerySchema = z
    .object({
    view: z.enum(['all', 'active', 'withdrawn']).default('all'),
    q: z.string().trim().max(200).default(''),
    category: z.string().trim().max(100).default(''),
    afterWithdrawalRank: z.enum(['0', '1']).optional(),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    const cursorFields = [value.afterWithdrawalRank, value.afterCreatedAt, value.afterId];
    const supplied = cursorFields.filter((field) => field !== undefined).length;
    if (supplied !== 0 && supplied !== cursorFields.length) {
        context.addIssue({
            code: 'custom',
            message: 'Document cursors require afterWithdrawalRank, afterCreatedAt, and afterId.',
        });
    }
});
export const documentWithdrawalInputSchema = z.object({
    reason: z.string().trim().min(2).max(1000),
});
const calendarEventDetailsSchema = z.object({
    title: z.string().trim().min(2).max(240),
    eventType: z.enum(calendarEventTypes).default('deadline'),
    startsAt: z.iso.datetime(),
    endsAt: optionalDateTime,
    allDay: z.boolean().default(false),
    location: optionalText,
    description: z.string().trim().max(3000).default(''),
});
const refineCalendarRange = (value, context) => {
    if (value.endsAt && new Date(value.endsAt) < new Date(value.startsAt)) {
        context.addIssue({ code: 'custom', message: 'The end must not be before the start.' });
    }
};
export const calendarEventInputSchema = calendarEventDetailsSchema
    .extend({ matterId: z.uuid().optional().or(z.literal('')) })
    .superRefine(refineCalendarRange);
export const calendarEventUpdateInputSchema = calendarEventDetailsSchema
    .extend({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
})
    .superRefine(refineCalendarRange);
export const calendarEventListQuerySchema = z
    .object({
    view: z.enum(['upcoming', 'past', 'cancelled', 'recent']).default('upcoming'),
    eventType: z.enum(['all', ...calendarEventTypes]).default('all'),
    q: z.string().trim().max(200).default(''),
    afterStartsAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterStartsAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Calendar cursors require both afterStartsAt and afterId.',
        });
    }
});
export const calendarEventHistoryQuerySchema = z
    .object({
    afterChangedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterChangedAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Calendar-history cursors require both afterChangedAt and afterId.',
        });
    }
});
export const matterActivityQuerySchema = z
    .object({
    afterOccurredAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterOccurredAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Matter-activity cursors require both afterOccurredAt and afterId.',
        });
    }
});
export const auditEventListQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    entityType: z.enum(['all', ...auditEntityTypes]).default('all'),
    occurredFrom: z.iso.datetime().optional(),
    occurredThrough: z.iso.datetime().optional(),
    afterOccurredAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterOccurredAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Audit cursors require both afterOccurredAt and afterId.',
        });
    }
    if (value.occurredFrom &&
        value.occurredThrough &&
        Date.parse(value.occurredFrom) > Date.parse(value.occurredThrough)) {
        context.addIssue({
            code: 'custom',
            path: ['occurredThrough'],
            message: 'Audit end time must not be before the start time.',
        });
    }
});
export const conflictCheckInputSchema = z.object({
    query: z.string().trim().min(2).max(200),
});
export const conflictCheckListQuerySchema = z
    .object({
    q: z.string().trim().max(200).default(''),
    afterCreatedAt: z.iso.datetime().optional(),
    afterId: z.uuid().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (Boolean(value.afterCreatedAt) !== Boolean(value.afterId)) {
        context.addIssue({
            code: 'custom',
            message: 'Conflict-check cursors require both afterCreatedAt and afterId.',
        });
    }
});
export const matterPartyInputSchema = z.object({
    matterId: z.uuid(),
    contactId: z.uuid(),
    role: z.enum(matterPartyRoles),
    relationshipDetail: optionalText,
});
export const matterPartyRemovalInputSchema = z.object({
    reason: z.string().trim().min(2).max(1000),
});
export const communicationInputSchema = z.object({
    matterId: z.uuid(),
    contactId: z.uuid().optional().or(z.literal('')),
    channel: z.enum(communicationChannels),
    direction: z.enum(communicationDirections),
    subject: z.string().trim().min(2).max(240),
    summary: z.string().trim().max(10_000).default(''),
    occurredAt: z.iso.datetime(),
});
export const communicationAmendmentInputSchema = communicationInputSchema
    .omit({ matterId: true })
    .extend({ reason: z.string().trim().min(2).max(1000) });
export const idParamSchema = z.object({ id: z.uuid() });
export const matterIdParamSchema = z.object({ matterId: z.uuid() });
export const taskCancellationInputSchema = z.object({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const timeEntryVoidInputSchema = z.object({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const timeEntryCorrectionInputSchema = timeEntryInputSchema
    .pick({ workDate: true, durationMinutes: true, description: true })
    .extend({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const calendarCancellationInputSchema = z.object({
    reason: z.string().trim().min(2).max(1000),
    expectedRecordVersion: z.number().int().min(1),
});
export const organizationPermissions = [
    'organization.settings.view',
    'organization.settings.update',
    'organization.users.view',
    'organization.users.invite',
    'organization.users.manage',
    'organization.billing.view',
    'organization.billing.manage',
    'organization.features.manage',
];
const organizationPermissionRoles = {
    'organization.settings.view': ['owner'],
    'organization.settings.update': ['owner'],
    'organization.users.view': ['owner'],
    'organization.users.invite': ['owner'],
    'organization.users.manage': ['owner'],
    'organization.billing.view': ['owner'],
    'organization.billing.manage': ['owner'],
    'organization.features.manage': ['owner'],
};
export function rolesForOrganizationPermission(permission) {
    return organizationPermissionRoles[permission];
}
export function hasOrganizationPermission(role, permission) {
    return Boolean(role && organizationPermissionRoles[permission].includes(role));
}
